"use client";

import React from "react";
import { MapPin, PhoneCall, Clock, ShieldCheck, ArrowRight, Navigation, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const serviceLocations = [{
  city: "Jonesboro (Headquarters)",
  county: "Clayton County",
  estTime: "15-30 mins",
  primaryCoverage: "Full 24/7 Emergency Dispatch",
  postalCodes: "30236, 30237, 30238"
}, {
  city: "Morrow",
  county: "Clayton County",
  estTime: "20-35 mins",
  primaryCoverage: "Residential & Commercial Plumbing",
  postalCodes: "30260, 30287"
}, {
  city: "Riverdale",
  county: "Clayton County",
  estTime: "20-35 mins",
  primaryCoverage: "Burst Pipes & Drain Emergencies",
  postalCodes: "30274, 30296"
}, {
  city: "Forest Park",
  county: "Clayton County",
  estTime: "25-40 mins",
  primaryCoverage: "Sewer Line & Water Main Repair",
  postalCodes: "30297, 30298"
}, {
  city: "Stockbridge",
  county: "Henry County",
  estTime: "25-40 mins",
  primaryCoverage: "Water Heater & Slab Leak Service",
  postalCodes: "30281"
}, {
  city: "Lovejoy & Hampton",
  county: "Clayton / Henry",
  estTime: "25-45 mins",
  primaryCoverage: "Emergency Repiping & Fixtures",
  postalCodes: "30250, 30228"
}];
const featuredServices = [{
  name: "Emergency Plumbing",
  path: "/emergency-plumbing/",
  description: "Immediate 24/7 dispatch for flooding, bursts, and critical leaks."
}, {
  name: "Pipe Repair & Leaks",
  path: "/pipe-repair/",
  description: "Accurate pinhole leak detection and durable copper/PEX repairs."
}, {
  name: "Drain Cleaning",
  path: "/drain-cleaning/",
  description: "Hydro jetting and heavy auger clearing for severe blockages."
}, {
  name: "Water Heater Repair",
  path: "/water-heater-repair/",
  description: "Rapid diagnostics and element replacement for instant hot water."
}, {
  name: "Sewer Line Repair",
  path: "/sewer-line-repair/",
  description: "Trenchless inspection and complete mainline restoration."
}, {
  name: "Toilet & Fixture Repair",
  path: "/toilet-repair/",
  description: "Fast repair of overflowing toilets, valves, sinks, and faucets."
}];
export default function ServiceArea() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicearea" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium text-xs tracking-wide uppercase bg-secondary/60">
            <MapPin className="mr-1 size-3.5 text-accent" />
            Clayton County & South Metro Atlanta
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            24/7 Emergency Plumbing Across Jonesboro & Surrounding Cities
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When burst pipes or backed-up drains threaten your property, our mobile response vans are strategically stationed in Jonesboro to arrive at your door rapidly.
          </p>
        </div>

        {/* Main Grid: Coverage Cards + Callout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          {/* Left Column: Interactive Area Cards */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold font-default text-foreground">
                  Primary Response Zones
                </h3>
                <p className="text-sm text-muted-foreground">
                  Average technician arrival times under normal traffic conditions
                </p>
              </div>
              <Badge variant="secondary" className="hidden sm:inline-flex gap-1.5 py-1">
                <Clock className="size-3.5 text-primary" />
                24/7 Active Fleet
              </Badge>
            </div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }}>
              {serviceLocations.map((item, i) => <motion.div key={item.city} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow duration-200">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-lg font-bold font-default text-foreground flex items-center gap-1.5">
                            <Navigation className="size-4 text-primary shrink-0" />
                            {item.city}
                          </CardTitle>
                          <CardDescription className="text-xs text-muted-foreground mt-0.5">
                            {item.county}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 text-2xs font-semibold">
                          {item.estTime}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-1">
                      <div className="text-xs text-foreground font-medium flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                        <span>{item.primaryCoverage}</span>
                      </div>
                      <div className="text-xs text-muted-foreground bg-muted/60 px-2.5 py-1.5 rounded-md font-mono">
                        Zip Codes: {item.postalCodes}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </motion.div>
          </div>

          {/* Right Column: Instant Dispatch CTA Card */}
          <div className="lg:col-span-4">
            <Card className="border-2 border-primary/20 bg-primary/5 shadow-md sticky top-24">
              <CardHeader>
                <div className="p-3 bg-primary text-primary-foreground rounded-lg w-fit mb-2 shadow-xs">
                  <PhoneCall className="size-6" />
                </div>
                <CardTitle className="text-2xl font-bold font-default text-foreground">
                  Need a Plumber Right Now?
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Do not let standing water damage your floors or drywall. Speak directly with our on-duty technician in Jonesboro.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <ShieldCheck className="size-4 text-accent shrink-0" />
                    <span>Licensed, bonded, and insured team</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>Upfront transparent pricing on arrival</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Clock className="size-4 text-accent shrink-0" />
                    <span>Zero extra charges for weekend calls</span>
                  </div>
                </div>

                <Separator className="my-2" />

                <div className="pt-1 flex flex-col gap-2.5">
                  <Button asChild size="lg" className="w-full font-semibold shadow-xs">
                    <Link to="/contact/">
                      <PhoneCall className="mr-2 size-4" />
                      Request Urgent Dispatch
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full">
                    <Link to="/service-areas/">
                      <span>View All Service Areas</span>
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Direct Service Links Grid */}
        <div className="mt-12 pt-12 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-1">
                <Wrench className="size-4 text-accent" />
                <span>Immediate On-Site Assistance</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                Direct Plumbing Services in Your Area
              </h3>
            </div>
            <Button asChild variant="ghost" className="w-fit text-primary font-semibold hover:text-primary">
              <Link to="/services/">
                See Full Service Catalog
                <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredServices.map((service, i) => <div key={service.name} data-index={i} className="group relative flex flex-col justify-between p-5 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all duration-200">
                <div>
                  <h4 className="text-lg font-bold font-default text-card-foreground group-hover:text-primary transition-colors">
                    {service.name}
                  </h4>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between">
                  <Link to={service.path} className="text-xs font-semibold text-primary inline-flex items-center gap-1 group-hover:underline">
                    <span>View details & book</span>
                    <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                  <Badge variant="secondary" className="text-2xs font-normal">
                    Available 24/7
                  </Badge>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}